import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import BadgeStatus from '../../components/common/BadgeStatus';
import Pagination from '../../components/common/Pagination';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import SortableTh, { nextSort } from '../../components/common/SortableTh';
import { FiSearch, FiPlus, FiX } from 'react-icons/fi';
import { getDefaultItemsPerPageSync } from '../../utils/settings';
import { getProjectManagementPaths } from '../../modules/projectManagement/config';
import { canFeatureForRole, PERMISSIONS } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { notifyApiError } from '../../utils/notify';
import { sortUsersByRole } from '../../utils/userSort';
import { getInitials } from '../../utils/getInitials';
import '../../styles/ProjectStatCards.css';

const PRIMARY_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const TEAMS_PURPLE = 'rgb(102, 126, 234)';
const TEAMS_TOOLBAR_BORDER = '#dee2e6';

const STATUSES = ['Not Started', 'In Progress', 'On Hold', 'Cancelled', 'Finished'];

// Display label shown to users — maps the stored API value to a human-readable name.
// The API/DB still stores 'Finished'; we just show 'Completed' in the UI.
const STATUS_DISPLAY_LABEL = {
  'Not Started': 'Not Started',
  'In Progress': 'In Progress',
  'On Hold': 'On Hold',
  Cancelled: 'Cancelled',
  Finished: 'Completed',
};

// Accent colour per status (aligned with the app status variants / dashboard palette).
const STATUS_META = {
  'Not Started': { accent: '#64748b', tint: 'rgba(100, 116, 139, 0.14)' },
  'In Progress': { accent: '#3b82f6', tint: 'rgba(59, 130, 246, 0.14)' },
  'On Hold': { accent: '#f59e0b', tint: 'rgba(245, 158, 11, 0.16)' },
  Cancelled: { accent: '#ef4444', tint: 'rgba(239, 68, 68, 0.14)' },
  Finished: { accent: '#10b981', tint: 'rgba(16, 185, 129, 0.16)' },
};
const DEADLINE_META = { accent: '#dc2626', tint: 'rgba(220, 38, 38, 0.14)' };

const getProjectStatusVariant = (status) => {
  const variantMap = {
    'Not Started': 'secondary',
    'In Progress': 'info',
    'On Hold': 'warning',
    Cancelled: 'danger',
    Finished: 'success',
  };
  return variantMap[status] || 'secondary';
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

const isCompletedProjectStatus = (status) => {
  const value = String(status || '').trim().toLowerCase();
  return value === 'finished' || value === 'completed' || value === 'complete';
};

// 👇 builds a map of managerId -> [directReportIds] from the users list (uses user.manager field)
const buildHierarchyMap = (users) => {
  const map = {};
  users.forEach((u) => {
    const userId = u._id || u.id;
    const managerId = (u.manager && (u.manager._id || u.manager.id || u.manager)) || '';
    if (!managerId) return;
    if (!map[managerId]) map[managerId] = [];
    map[managerId].push(userId);
  });
  return map;
};

// 👇 walks the hierarchy map to find every user under a given leader (direct + indirect reports)
const getDescendantIds = (leaderId, hierarchyMap) => {
  if (!leaderId) return [];
  const result = [];
  const queue = [...(hierarchyMap[leaderId] || [])];
  while (queue.length) {
    const current = queue.shift();
    if (result.includes(current)) continue;
    result.push(current);
    if (hierarchyMap[current]) queue.push(...hierarchyMap[current]);
  }
  return result;
};

const ProjectsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectManagementPaths = getProjectManagementPaths(
    location.pathname.startsWith('/employee') ? 'employee' : 'admin'
  );
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (_) {
      return {};
    }
  }, []);
  const currentUserId = String(currentUser?.id || currentUser?._id || '').trim();
  const currentUserRole = String(currentUser?.role || '').toLowerCase();
  const isManagerLike = ['admin', 'hod', 'manager'].includes(currentUserRole);

  // Settings → User Roles matrix drives which action buttons show (matches backend enforcement).
  const { rolePermissions } = useRolePermissionMatrix();
  const canAddProject = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.ADD_PROJECT]);
  const canEditProjectPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.EDIT_PROJECT]);
  const canDeleteProjectPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.DELETE_PROJECT]);
  // "View All Projects" lets any role (incl. admin) see every user's projects. Powers the All Users Projects tab.
  const hasViewAllProjectsPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.VIEW_ALL_PROJECTS]);

  // Edit is governed purely by the Edit Project permission (matches the backend, which checks
  // edit_project by role — not project membership). Anyone granted edit_project can edit any project.
  const [projects, setProjects] = useState([]);
  const [summaryProjects, setSummaryProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  // Column sort, applied server-side across all pages. Empty sortBy = default (newest first).
  const [sort, setSort] = useState({ sortBy: '', sortOrder: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  // Members source: 'hierarchy' = only the Project Leader's team, 'all' = any user in the system.
  const [memberSource, setMemberSource] = useState('hierarchy');
  const [followerSearch, setFollowerSearch] = useState('');
  const [followerDropdownOpen, setFollowerDropdownOpen] = useState(false);
  // Followers source: 'all' = pick from every user, 'member' = pick only from the project's members.
  const [followerSource, setFollowerSource] = useState('all');
  const [leaderSearch, setLeaderSearch] = useState('');
  const [leaderDropdownOpen, setLeaderDropdownOpen] = useState(false);
  const memberDropdownRef = useRef(null);
  const followerDropdownRef = useRef(null);
  const leaderDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target)) {
        setMemberDropdownOpen(false);
      }
      if (followerDropdownRef.current && !followerDropdownRef.current.contains(event.target)) {
        setFollowerDropdownOpen(false);
      }
      if (leaderDropdownRef.current && !leaderDropdownRef.current.contains(event.target)) {
        setLeaderDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const [activeTab, setActiveTab] = useState('my');
  // Single "My + Team" view: [] shows own + all subordinate projects; specific ids narrow to those members.
  const [memberFilter, setMemberFilter] = useState([]);
  // All Users Projects tab filters (by project leader and by member).
  const [leaderFilter, setLeaderFilter] = useState([]);
  const [allMemberFilter, setAllMemberFilter] = useState([]);
  // Drives the Reset Filters button: red while something the button would clear is set, grey/disabled otherwise.
  const hasActiveFilters =
    Boolean(search.trim()) ||
    statusFilter.length > 0 ||
    overdueOnly ||
    memberFilter.length > 0 ||
    leaderFilter.length > 0 ||
    allMemberFilter.length > 0;
  const [usersForHierarchy, setUsersForHierarchy] = useState([]);
  const [projectForm, setProjectForm] = useState({
    name: '',
    status: 'Not Started',
    startDate: '',
    deadline: '',
    totalEstimatedEffort: 0,
    totalActualEffort: 0,
    teamLeader: '',
    description: '',
    members: [],
    followers: [],
  });
  const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState('');
  const [deletingProjectId, setDeletingProjectId] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);

  const canSeeTeamTab = ['admin', 'hod', 'manager', 'team-leader'].includes(currentUserRole);
  // "All Users Projects" tab is governed purely by the View All Projects permission (matrix), for every
  // role including admin — so unchecking it in Settings actually hides the tab.
  const showAllUsersProjectsTab = hasViewAllProjectsPerm;

  const subordinateIds = useMemo(() => {
    if (!currentUserId || !canSeeTeamTab) return [];
    // Walk the manager chain to collect direct + indirect reports (applies to admin too, so the merged
    // My + Team view shows only the user's actual hierarchy — the All Users Projects tab covers everyone).
    const result = [];
    const visited = new Set();
    const queue = [currentUserId];
    while (queue.length) {
      const managerId = queue.shift();
      if (visited.has(managerId)) continue;
      visited.add(managerId);
      usersForHierarchy.forEach((u) => {
        const uid = String(u._id || u.id || '');
        const managerRef = String(u.manager?._id || u.manager?.id || u.manager || '');
        if (managerRef === managerId && uid !== currentUserId) {
          result.push(uid);
          queue.push(uid);
        }
      });
    }
    return result;
  }, [usersForHierarchy, currentUserId, canSeeTeamTab]);

  // No tabs: project visibility is driven purely by the View All Projects permission.
  // Has permission  → 'all'  → every user's projects.
  // No permission   → 'my'   → the user's own hierarchy (self + all subordinates).
  useEffect(() => {
    setActiveTab(showAllUsersProjectsTab ? 'all' : 'my');
  }, [showAllUsersProjectsTab]);

  // Builds the member/leader/scope query params so filtering happens server-side (full dataset),
  // matching the active tab and the selected filters.
  const buildProjectScopeParams = () => {
    const params = {};
    if (activeTab === 'all' && showAllUsersProjectsTab) {
      if (leaderFilter.length > 0) params.leaderId = leaderFilter.join(',');
      if (allMemberFilter.length > 0) params.projectMemberId = allMemberFilter.join(',');
      return params;
    }
    // Merged "My + Team" view.
    if (memberFilter.length > 0) {
      params.memberId = memberFilter.join(',');
    } else {
      params.teamMemberIds = [currentUserId, ...subordinateIds].filter(Boolean).join(',');
    }
    return params;
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Status/overdue summary tiles — fetched server-side across all pages, scoped to the active tab +
  // member/leader filters (but NOT status/overdue themselves, so each card shows its own count).
  const loadProjectsSummary = async () => {
    try {
      const scopeParams = buildProjectScopeParams();
      const firstPage = await api.getProjects({ page: 1, limit: 200, ...scopeParams });
      if (!firstPage?.success) return;

      const total = firstPage.total || 0;
      const totalPagesLocal = Math.max(1, firstPage.totalPages || Math.ceil(total / 200) || 1);
      const allProjects = [...(firstPage.data || [])];

      if (totalPagesLocal > 1) {
        const requests = [];
        for (let page = 2; page <= totalPagesLocal; page += 1) {
          requests.push(api.getProjects({ page, limit: 200, ...scopeParams }));
        }
        const responses = await Promise.all(requests);
        responses.forEach((response) => {
          if (response?.success) {
            allProjects.push(...(response.data || []));
          }
        });
      }

      setSummaryProjects(allProjects);
    } catch (error) {
      notifyApiError(error, 'Failed to load project summary');
    }
  };

  // Reload the summary whenever the scope (tab / member / leader filters) changes.
  useEffect(() => {
    loadProjectsSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, memberFilter, leaderFilter, allMemberFilter, subordinateIds]);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, search, statusFilter, activeTab, memberFilter, leaderFilter, allMemberFilter, overdueOnly, subordinateIds, sort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, activeTab, memberFilter, leaderFilter, allMemberFilter, sort]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.getProjects({
        page: currentPage,
        limit: itemsPerPage,
        search: search || undefined,
        status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
        overdue: overdueOnly ? 'true' : undefined,
        sortBy: sort.sortBy || undefined,
        sortOrder: sort.sortBy ? sort.sortOrder : undefined,
        ...buildProjectScopeParams(),
      });
      if (response.success) {
        setProjects(response.data || []);
        setTotalProjects(response.total || 0);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      notifyApiError(error, 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // 👇 fetch ALL users regardless of role, so hierarchy (admin > hod > manager > team-leader > employee) is fully available
  const loadUsers = async () => {
    try {
      const response = await api.getUsers({ limit: 1000 });
      if (!response.success) return;
      const allUsers = response.data || [];
      setAllUsers(allUsers);
      setUsersForHierarchy(allUsers);
    } catch (error) {
      notifyApiError(error, 'Failed to load users');
    }
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [currentPage, totalPages]);

  // summaryProjects is already server-scoped to the active tab + member/leader filters (see
  // loadProjectsSummary), so the tiles just tally it directly.
  const statusSummary = useMemo(() => {
    const summary = STATUSES.reduce((acc, current) => ({ ...acc, [current]: 0 }), {});
    summaryProjects.forEach((project) => {
      summary[project.status] = (summary[project.status] || 0) + 1;
    });
    return summary;
  }, [summaryProjects]);

  const overdueProjectsCount = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return summaryProjects.reduce((count, project) => {
      if (isCompletedProjectStatus(project.status)) return count;
      if (!project.deadline) return count;
      const deadlineDate = new Date(project.deadline);
      if (Number.isNaN(deadlineDate.getTime())) return count;
      deadlineDate.setHours(0, 0, 0, 0);
      return deadlineDate < today ? count + 1 : count;
    }, 0);
  }, [summaryProjects]);

  // The backend already applies tab scope + member/leader + status + overdue filters and paginates,
  // so the fetched page is exactly what to render.
  const visibleProjects = projects;

  // 👇 full manager -> reports hierarchy map, built once from allUsers
  const hierarchyMap = useMemo(() => buildHierarchyMap(allUsers), [allUsers]);

  // 👇 every user (direct + indirect) under the currently selected Project Leader
  const teamLeaderDescendantIds = useMemo(
    () => getDescendantIds(projectForm.teamLeader, hierarchyMap),
    [projectForm.teamLeader, hierarchyMap]
  );

  const selectedUsers = useMemo(
    () => allUsers.filter((user) => projectForm.members.includes(user._id || user.id)),
    [allUsers, projectForm.members]
  );

  // Live mirrors of the current selections. The dropdown list memos below read these instead of
  // depending on projectForm.members / projectForm.followers directly, so that ticking a checkbox
  // never re-runs them — see the ordering comments in filteredUsers / filteredFollowerUsers.
  const memberSelectionRef = useRef(projectForm.members);
  memberSelectionRef.current = projectForm.members;
  const followerSelectionRef = useRef(projectForm.followers);
  followerSelectionRef.current = projectForm.followers;

  // 👇 members list is restricted to whoever falls under the selected Project Leader's hierarchy
  const eligibleMemberUsers = useMemo(() => {
    if (!projectForm.teamLeader) return [];
    return allUsers.filter((user) => teamLeaderDescendantIds.includes(user._id || user.id));
  }, [allUsers, teamLeaderDescendantIds, projectForm.teamLeader]);

  const filteredUsers = useMemo(() => {
    const selectedSet = new Set((memberSelectionRef.current || []).map((id) => String(id)));
    // 'all' source drops the hierarchy restriction. In 'hierarchy' mode we still keep already-selected
    // users in the list so members added outside the hierarchy stay visible (and removable).
    const baseList =
      memberSource === 'all'
        ? allUsers
        : [
            ...eligibleMemberUsers,
            ...allUsers.filter(
              (user) =>
                selectedSet.has(String(user._id || user.id)) &&
                !eligibleMemberUsers.some((eligible) => String(eligible._id || eligible.id) === String(user._id || user.id))
            ),
          ];
    const value = memberSearch.trim().toLowerCase();
    const list = !value
      ? baseList
      : baseList.filter((user) =>
          `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(value)
        );
    const sorted = sortUsersByRole(list);
    // Selected members float to the top so the current selection is always visible first. This order is
    // snapshotted when the dropdown opens or the search/source changes — NOT on every toggle — otherwise
    // ticking a checkbox reshuffles the rows and loses the user's scroll position mid-selection.
    const selected = sorted.filter((user) => selectedSet.has(String(user._id || user.id)));
    const rest = sorted.filter((user) => !selectedSet.has(String(user._id || user.id)));
    return [...selected, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, eligibleMemberUsers, memberSource, memberSearch, memberDropdownOpen]);

  // 👇 Followers are NOT restricted by hierarchy - any user can follow a project
  const selectedFollowers = useMemo(
    () => allUsers.filter((user) => projectForm.followers.includes(user._id || user.id)),
    [allUsers, projectForm.followers]
  );

  const filteredFollowerUsers = useMemo(() => {
    // 'member' source restricts follower options to the selected Project Leader's whole team (hierarchy).
    const baseList = followerSource === 'member' ? eligibleMemberUsers : allUsers;
    const value = followerSearch.trim().toLowerCase();
    const list = !value
      ? baseList
      : baseList.filter((user) =>
          `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(value)
        );
    const sorted = sortUsersByRole(list);
    // Selected followers float to the top so the current selection is always visible first. This order is
    // snapshotted when the dropdown opens or the search/source changes — NOT on every toggle — otherwise
    // ticking a checkbox reshuffles the rows and loses the user's scroll position mid-selection.
    const selectedSet = new Set((followerSelectionRef.current || []).map((id) => String(id)));
    const selected = sorted.filter((user) => selectedSet.has(String(user._id || user.id)));
    const rest = sorted.filter((user) => !selectedSet.has(String(user._id || user.id)));
    return [...selected, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, eligibleMemberUsers, followerSource, followerSearch, followerDropdownOpen]);

  // 👇 Project Leader dropdown, ordered by role (Admin → HOD → Manager → Team Leader → Employee)
  const sortedLeaderUsers = useMemo(() => sortUsersByRole(allUsers), [allUsers]);
  // 👇 searchable Project Leader list — filtered by the leader search box (name / email / role)
  const filteredLeaderUsers = useMemo(() => {
    const value = leaderSearch.trim().toLowerCase();
    if (!value) return sortedLeaderUsers;
    return sortedLeaderUsers.filter((user) =>
      `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(value)
    );
  }, [sortedLeaderUsers, leaderSearch]);

  const resetProjectModal = () => {
    setShowProjectModal(false);
    setEditingProjectId('');
    setMemberSearch('');
    setMemberDropdownOpen(false);
    setMemberSource('hierarchy');
    setFollowerSearch('');
    setFollowerDropdownOpen(false);
    setFollowerSource('all');
    setLeaderSearch('');
    setLeaderDropdownOpen(false);
    setProjectForm({
      name: '',
      status: 'Not Started',
      startDate: '',
      deadline: '',
      totalEstimatedEffort: 0,
      totalActualEffort: 0,
      teamLeader: '',
      description: '',
      members: [],
      followers: [],
    });
  };

  const openAddProjectModal = () => {
    resetProjectModal();
    setShowProjectModal(true);
  };

  const openEditProjectModal = async (projectId) => {
    try {
      setSavingProject(true);
      const response = await api.getProject(projectId);
      if (!response.success || !response.data) {
        toast.error('Project details not found');
        return;
      }
      const project = response.data;
      setEditingProjectId(projectId);
      setProjectForm({
        name: project.name || '',
        status: project.status || 'Not Started',
        startDate: project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : '',
        deadline: project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : '',
        totalEstimatedEffort: project.totalEstimatedEffort || 0,
        totalActualEffort: project.totalActualEffort || 0,
        teamLeader: project.teamLeader?._id || project.teamLeader?.id || '',
        description: project.description || '',
        members: (project.members || []).map((member) => member._id || member.id),
        followers: (project.followers || []).map((follower) => follower._id || follower.id),
      });
      setMemberSearch('');
      setMemberDropdownOpen(false);
      setMemberSource('hierarchy');
      setFollowerSearch('');
      setFollowerDropdownOpen(false);
      setFollowerSource('all');
      setShowProjectModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load project');
    } finally {
      setSavingProject(false);
    }
  };

  const toggleMember = (memberId) => {
    setProjectForm((prev) => {
      const exists = prev.members.includes(memberId);
      return {
        ...prev,
        members: exists ? prev.members.filter((id) => id !== memberId) : [...prev.members, memberId],
      };
    });
  };

  const toggleFollower = (followerId) => {
    setProjectForm((prev) => {
      const exists = prev.followers.includes(followerId);
      return {
        ...prev,
        followers: exists ? prev.followers.filter((id) => id !== followerId) : [...prev.followers, followerId],
      };
    });
  };

  // Only the hierarchy source needs a leader first — 'all' can be picked from straight away.
  const memberPickerLocked = memberSource === 'hierarchy' && !projectForm.teamLeader;

  const memberIdsFiltered = filteredUsers.map((user) => user._id || user.id);
  const allMembersSelected = memberIdsFiltered.length > 0 && memberIdsFiltered.every((id) => projectForm.members.includes(id));
  const toggleSelectAllMembers = () => {
    setProjectForm((prev) => {
      const all = memberIdsFiltered.every((id) => prev.members.includes(id));
      return {
        ...prev,
        members: all
          ? prev.members.filter((id) => !memberIdsFiltered.includes(id))
          : [...new Set([...prev.members, ...memberIdsFiltered])],
      };
    });
  };

  const followerIdsFiltered = filteredFollowerUsers.map((user) => user._id || user.id);
  const allFollowersSelected = followerIdsFiltered.length > 0 && followerIdsFiltered.every((id) => projectForm.followers.includes(id));
  const toggleSelectAllFollowers = () => {
    setProjectForm((prev) => {
      const all = followerIdsFiltered.every((id) => prev.followers.includes(id));
      return {
        ...prev,
        followers: all
          ? prev.followers.filter((id) => !followerIdsFiltered.includes(id))
          : [...new Set([...prev.followers, ...followerIdsFiltered])],
      };
    });
  };

  const handleSaveProject = async () => {
    if (!projectForm.name.trim() || !projectForm.startDate || !projectForm.deadline) {
      toast.warning('Project name, start date and deadline are required');
      return;
    }
    {
      const start = new Date(projectForm.startDate);
      const deadline = new Date(projectForm.deadline);
      start.setHours(0, 0, 0, 0);
      deadline.setHours(0, 0, 0, 0);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(deadline.getTime()) && deadline < start) {
        toast.warning('Deadline cannot be before the start date');
        return;
      }
    }
    try {
      setSavingProject(true);
      const payload = {
        ...projectForm,
        totalEstimatedEffort: Number(projectForm.totalEstimatedEffort || 0),
        totalActualEffort: Number(projectForm.totalActualEffort || 0),
      };
      if (!editingProjectId) {
        payload.progress = 0;
      }
      if (editingProjectId) {
        await api.updateProject(editingProjectId, payload);
        toast.success('Project updated');
      } else {
        await api.createProject(payload);
        toast.success('Project created');
      }
      resetProjectModal();
      loadProjectsSummary();
      fetchProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to save project');
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!projectId) return;
    try {
      setDeletingProjectId(projectId);
      await api.deleteProject(projectId);
      toast.success('Project moved to trash');
      setProjects((prev) => prev.filter((project) => project._id !== projectId));
      setConfirmDeleteProjectId('');
      loadProjectsSummary();
      fetchProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to delete project');
    } finally {
      setDeletingProjectId('');
    }
  };

  // Inline status change straight from the table (mirrors the Tasks list behaviour) — optimistic
  // update with rollback on failure.
  const updateProjectStatusFromTable = async (project, targetStatus) => {
    const id = String(project?._id || '');
    if (!id || !targetStatus || project.status === targetStatus) return;

    const prevProjects = projects;
    setProjects((prev) =>
      prev.map((row) => (String(row._id) === id ? { ...row, status: targetStatus } : row))
    );
    setStatusUpdatingId(id);

    try {
      await api.updateProject(id, { status: targetStatus });
      toast.success(`Status updated to ${targetStatus}`);
      loadProjectsSummary();
    } catch (error) {
      setProjects(prevProjects);
      toast.error(error.message || 'Failed to update project status');
    } finally {
      setStatusUpdatingId('');
    }
  };

  const buildExportRows = () =>
    visibleProjects.map((project) => ({
      projectId: project.projectCode || (project.projectNo ? `P${String(project.projectNo).padStart(4, '0')}` : '-'),
      projectName: project.name || '-',
      startDate: formatDate(project.startDate),
      deadline: formatDate(project.deadline),
      teamLeader: project.teamLeader?.name || '-',
      members: (project.members || []).map((member) => member.name || '-').join(', ') || '-',
      followers: (project.followers || []).map((follower) => follower.name || '-').join(', ') || '-',
      status: project.status || '-',
    }));

  const toCsv = (rows) => {
    const headers = ['Project ID', 'Project Name', 'Start Date', 'Deadline', 'Team Leader', 'Members', 'Followers', 'Status'];
    const body = rows.map((row) => [
      row.projectId,
      row.projectName,
      row.startDate,
      row.deadline,
      row.teamLeader,
      row.members,
      row.followers,
      row.status,
    ]);
    return [headers, ...body]
      .map((line) => line.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
  };

  const downloadFile = (content, mimeType, fileName) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = (type) => {
    setShowExportMenu(false);
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info('No projects to export');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 10);
    const csv = toCsv(rows);

    if (type === 'csv') {
      // BOM + sep line helps Excel parse columns correctly on many locales.
      const csvForExcel = `\uFEFFsep=,\n${csv}`;
      downloadFile(csvForExcel, 'text/csv;charset=utf-8;', `projects-${timestamp}.csv`);
      return;
    }

    if (type === 'excel') {
      const excelRows = rows
        .map(
          (row) => `<tr>
  <td>${row.projectId}</td>
  <td>${row.projectName}</td>
  <td>${row.startDate}</td>
  <td>${row.deadline}</td>
  <td>${row.teamLeader}</td>
  <td>${row.members}</td>
  <td>${row.followers}</td>
  <td>${row.status}</td>
</tr>`
        )
        .join('');
      const excelHtml = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #cbd5e0; padding: 6px; font-size: 12px; text-align: left; }
    th { background: #edf2f7; font-weight: 700; }
  </style>
</head>
<body>
  <table>
    <thead>
      <tr>
        <th>Project ID</th><th>Project Name</th><th>Start Date</th>
        <th>Deadline</th><th>Team Leader</th><th>Members</th><th>Followers</th><th>Status</th>
      </tr>
    </thead>
    <tbody>${excelRows}</tbody>
  </table>
</body>
</html>`;
      downloadFile(excelHtml, 'application/vnd.ms-excel;charset=utf-8;', `projects-${timestamp}.xls`);
      return;
    }

    const tableRows = rows
      .map(
        (row) => `<tr>
  <td>${row.projectId}</td>
  <td>${row.projectName}</td>
  <td>${row.startDate}</td>
  <td>${row.deadline}</td>
  <td>${row.teamLeader}</td>
  <td>${row.members}</td>
  <td>${row.followers}</td>
  <td>${row.status}</td>
</tr>`
      )
      .join('');

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Projects Export</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h2 { margin: 0 0 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 8px; font-size: 12px; text-align: left; }
    th { background: #f3f4f6; }
  </style>
</head>
<body>
  <h2>Projects Export (${timestamp})</h2>
  <table>
    <thead>
      <tr>
        <th>Project ID</th><th>Project Name</th><th>Start Date</th>
        <th>Deadline</th><th>Team Leader</th><th>Members</th><th>Followers</th><th>Status</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Unable to open export window');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    if (type === 'pdf') {
      toast.info('Use "Save as PDF" in print dialog');
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div className="pj-stat-grid" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
        {STATUSES.map((status) => {
          const meta = STATUS_META[status] || STATUS_META['Not Started'];
          const active = statusFilter.includes(status) && !overdueOnly;
          const toggleStatus = () => {
            setOverdueOnly(false);
            setStatusFilter((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
            setCurrentPage(1);
          };
          return (
            <div
              key={status}
              onClick={(e) => {
                if (e.target.type === 'checkbox') return;
                toggleStatus();
              }}
              className={`pj-stat-card${active ? ' is-active' : ''}`}
              style={{ '--accent': meta.accent, '--tint': meta.tint }}
            >
              <div className="pj-stat-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleStatus();
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: meta.accent, flexShrink: 0 }}
                />
                <span className="pj-stat-label">{STATUS_DISPLAY_LABEL[status] || status}</span>
              </div>
              <div className="pj-stat-value">{statusSummary[status] || 0}</div>
            </div>
          );
        })}
        <div
          onClick={() => {
            setOverdueOnly((prev) => !prev);
            setStatusFilter([]);
            setCurrentPage(1);
          }}
          className={`pj-stat-card is-alert${overdueOnly ? ' is-active' : ''}`}
          style={{ '--accent': DEADLINE_META.accent, '--tint': DEADLINE_META.tint }}
        >
          <div className="pj-stat-top">
            <span className="pj-stat-label">Overdue</span>
          </div>
          <div className="pj-stat-value">{overdueProjectsCount}</div>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: '16px'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            alignItems: 'center',
            padding: '14px 16px',
            flexWrap: 'nowrap',
            overflowX: 'auto'
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flex: 1, minWidth: 0, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '0 0 auto', width: 188 }}>
              <FiSearch
                size={14}
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#98a2b3',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search Project Name"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  height: 34,
                  padding: '0 10px 0 32px',
                  border: `1px solid ${TEAMS_TOOLBAR_BORDER}`,
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#344054',
                  background: '#fff',
                }}
              />
            </div>
            <div style={{ flex: '0 0 auto', width: 172, minWidth: 0 }}>
              <MultiSelectDropdown
                placeholder="All Status"
                options={STATUSES}
                selectedValues={statusFilter}
                onChange={(v) => {
                  setStatusFilter(v);
                  setOverdueOnly(false);
                }}
                getLabel={(status) => STATUS_DISPLAY_LABEL[status] || status}
                showSearch={false}
                showSelectAll={false}
              />
            </div>
            {activeTab !== 'all' && subordinateIds.length > 0 && (
              <div style={{ flex: '0 0 auto', width: 172, minWidth: 0 }}>
                <MultiSelectDropdown
                  placeholder="All Members"
                  options={[currentUserId, ...subordinateIds]}
                  selectedValues={memberFilter}
                  onChange={setMemberFilter}
                  getLabel={(id) => {
                    if (id === currentUserId) return 'Me (You)';
                    const u = allUsers.find((user) => String(user._id || user.id) === id);
                    return u ? `${u.name} (${u.role || '-'})` : id;
                  }}
                  showSearch={false}
                  showSelectAll={false}
                />
              </div>
            )}
            {activeTab === 'all' && showAllUsersProjectsTab && (
              <>
                <div style={{ flex: '0 0 auto', width: 172, minWidth: 0 }}>
                  <MultiSelectDropdown
                    placeholder="All Project Leaders"
                    options={sortedLeaderUsers.map((user) => String(user._id || user.id))}
                    selectedValues={leaderFilter}
                    onChange={setLeaderFilter}
                    getLabel={(id) => {
                      const u = sortedLeaderUsers.find((user) => String(user._id || user.id) === id);
                      return u ? `${u.name || id} (${u.role || '-'})` : id;
                    }}
                    showSearch
                    showSelectAll={false}
                    showSelectAllCheckbox
                  />
                </div>
                <div style={{ flex: '0 0 auto', width: 172, minWidth: 0 }}>
                  <MultiSelectDropdown
                    placeholder="All Members"
                    options={sortedLeaderUsers.map((user) => String(user._id || user.id))}
                    selectedValues={allMemberFilter}
                    onChange={setAllMemberFilter}
                    getLabel={(id) => {
                      const u = sortedLeaderUsers.find((user) => String(user._id || user.id) === id);
                      return u ? `${u.name || id} (${u.role || '-'})` : id;
                    }}
                    showSearch
                    showSelectAll={false}
                    showSelectAllCheckbox
                  />
                </div>
              </>
            )}
            <button
              type="button"
              disabled={!hasActiveFilters}
              title={hasActiveFilters ? 'Clear all filters' : 'No filters applied'}
              onClick={() => {
                setSearch('');
                setStatusFilter([]);
                setOverdueOnly(false);
                setMemberFilter([]);
                setLeaderFilter([]);
                setAllMemberFilter([]);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                height: 34,
                padding: '0 14px',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: hasActiveFilters ? 'rgb(254, 215, 215)' : '#edf2f7',
                color: hasActiveFilters ? 'rgb(197, 48, 48)' : '#a0aec0',
                cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
              }}
            >
              <FiX
                size={14}
                strokeWidth={2.25}
                aria-hidden
                style={{ color: hasActiveFilters ? 'rgb(197, 48, 48)' : '#a0aec0' }}
              />
              Reset Filters
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flexShrink: 0, alignItems: 'center' }}>
            {canAddProject && (
            <button
              type="button"
              onClick={openAddProjectModal}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                height: 34,
                padding: '0 14px',
                border: 'none',
                background: PRIMARY_GRADIENT,
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: '#ffffff',
              }}
            >
              <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
              Add Project
            </button>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1020px', borderCollapse: 'collapse' }}>
            <thead style={{ background: PRIMARY_GRADIENT }}>
              <tr>
                {[
                  ['projectId', 'Project ID'],
                  ['name', 'Project Name'],
                  ['status', 'Status'],
                  ['startDate', 'Start Date'],
                  ['deadline', 'Deadline'],
                  ['leader', 'Project Leader'],
                  ['members', 'Members'],
                  ['followers', 'Followers'],
                ].map(([field, label]) => (
                  <SortableTh
                    key={field}
                    field={field}
                    label={label}
                    sort={sort}
                    onSort={(f) => setSort((prev) => nextSort(prev, f))}
                    style={thStyle}
                  />
                ))}
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleProjects.map((project) => {
                return (
                  <tr key={project._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => navigate(projectManagementPaths.projectView(project._id))}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          margin: 0,
                          color: '#3182ce',
                          fontWeight: 700,
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '13px',
                        }}
                        title="Open project details"
                      >
                        {project.projectCode || (project.projectNo ? `P${String(project.projectNo).padStart(4, '0')}` : '-')}
                      </button>
                    </td>
                    <td style={tdStyle}>{project.name || '-'}</td>
                    <td style={tdStyle}>
                      {canEditProjectPerm ? (
                        <select
                          value={project.status}
                          disabled={statusUpdatingId === String(project._id)}
                          onChange={(event) => updateProjectStatusFromTable(project, event.target.value)}
                          style={{
                            height: '30px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '0 6px',
                            fontSize: '12px',
                            color: '#2d3748',
                            background: '#fff',
                            cursor: statusUpdatingId === String(project._id) ? 'wait' : 'pointer',
                          }}
                        >
                          {STATUSES.map((status) => (
                            <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                          ))}
                        </select>
                      ) : (
                        <BadgeStatus
                          status={STATUS_DISPLAY_LABEL[project.status] || project.status}
                          variant={getProjectStatusVariant(project.status)}
                          style={{ borderRadius: '6px', padding: '4px 10px' }}
                        />
                      )}
                    </td>
                    <td style={tdStyle}>{formatDate(project.startDate)}</td>
                    <td style={tdStyle}>{formatDate(project.deadline)}</td>
                    <td style={tdStyle}>{project.teamLeader?.name || '-'}</td>
                    <td
                      style={tdStyle}
                      title={(project.members || [])
                        .map((member) => member?.name || member?.email || 'Unknown User')
                        .filter(Boolean)
                        .join(', ')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                        {(project.members || []).slice(0, (project.members || []).length > 3 ? 2 : 3).map((member) => (
                          <div
                            key={member._id || member.id}
                            title={member?.name || member?.email || 'Unknown User'}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700
                            }}
                          >
                            {getInitials(member.name)}
                          </div>
                        ))}
                        {(project.members || []).length > 3 && (
                          <div
                            title={(project.members || [])
                              .slice(2)
                              .map((member) => member?.name || member?.email || 'Unknown User')
                              .join(', ')}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700
                            }}
                          >
                            +{(project.members || []).length - 2}
                          </div>
                        )}
                      </div>
                    </td>
                    <td
                      style={tdStyle}
                      title={(project.followers || [])
                        .map((follower) => follower?.name || follower?.email || 'Unknown User')
                        .filter(Boolean)
                        .join(', ')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                        {(project.followers || []).slice(0, (project.followers || []).length > 3 ? 2 : 3).map((follower) => (
                          <div
                            key={follower._id || follower.id}
                            title={follower?.name || follower?.email || 'Unknown User'}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #9f7aea 0%, #6b46c1 100%)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700
                            }}
                          >
                            {getInitials(follower.name)}
                          </div>
                        ))}
                        {(project.followers || []).length > 3 && (
                          <div
                            title={(project.followers || [])
                              .slice(2)
                              .map((follower) => follower?.name || follower?.email || 'Unknown User')
                              .join(', ')}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #9f7aea 0%, #6b46c1 100%)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700
                            }}
                          >
                            +{(project.followers || []).length - 2}
                          </div>
                        )}
                        {(!project.followers || project.followers.length === 0) && (
                          <span style={{ fontSize: '12px', color: '#a0aec0' }}>-</span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                        {canEditProjectPerm && (
                          <button
                            type="button"
                            style={actionBtnStyle('#e6fffa', '#234e52')}
                            title="Edit"
                            onClick={() => openEditProjectModal(project._id)}
                          >
                            ✏️
                          </button>
                        )}
                        {canDeleteProjectPerm && (
                          <button
                            type="button"
                            style={actionBtnStyle('#fff5f5', '#c53030')}
                            title="Delete"
                            onClick={() => setConfirmDeleteProjectId(project._id)}
                            disabled={deletingProjectId === project._id}
                          >
                            {deletingProjectId === project._id ? '…' : '🗑️'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleProjects.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: '#718096' }}>
                    {loading ? 'Loading projects...' : 'No projects found for selected filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!overdueOnly && (
          <div style={{ padding: '0 16px 14px' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalProjects}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>

      {showProjectModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalCardStyle, width: '760px', maxWidth: '94vw' }} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#2d3748' }}>
                {editingProjectId ? 'Edit Project' : 'Add New Project'}
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={resetProjectModal}
                style={modalCloseBtnStyle}
              >×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'start' }}>
              {/* Row 1: Project Name | Project Leader */}
              <div>
                <div style={fieldLabelStyle}>Project Name</div>
                <input
                  required
                  placeholder="Project name"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm((prev) => ({ ...prev, name: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div ref={leaderDropdownRef} style={{ position: 'relative' }}>
                <div style={fieldLabelStyle}>Project Leader</div>
                <button
                  type="button"
                  onClick={() => setLeaderDropdownOpen((prev) => !prev)}
                  style={{ ...memberDropdownTriggerStyle }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const selectedLeader = sortedLeaderUsers.find(
                        (user) => String(user._id || user.id) === String(projectForm.teamLeader)
                      );
                      return selectedLeader
                        ? `${selectedLeader.name || 'Unnamed User'} (${selectedLeader.role || '-'})`
                        : 'Select project leader';
                    })()}
                  </span>
                  <span style={{ fontSize: '12px', color: '#718096' }}>{leaderDropdownOpen ? '▲' : '▼'}</span>
                </button>

                {leaderDropdownOpen && (
                  <div style={memberDropdownPanelStyle}>
                    <input
                      autoFocus
                      placeholder="Search project leader..."
                      value={leaderSearch}
                      onChange={(e) => setLeaderSearch(e.target.value)}
                      style={{ ...inputStyle, width: '100%', marginBottom: '8px' }}
                    />
                    <div style={{ maxHeight: '220px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                      {filteredLeaderUsers.length === 0 && (
                        <div style={{ fontSize: '12px', color: '#718096', padding: '6px 2px' }}>
                          No matching users.
                        </div>
                      )}
                      {filteredLeaderUsers.map((user) => {
                        const userId = user._id || user.id;
                        const selected = String(userId) === String(projectForm.teamLeader);
                        return (
                          <div
                            key={userId}
                            onClick={() => {
                              // 👇 changing the leader resets members, since the eligible pool (hierarchy) changes
                              setProjectForm((prev) => ({
                                ...prev,
                                teamLeader: userId,
                                members: userId ? [userId] : [],
                              }));
                              setMemberSearch('');
                              setLeaderDropdownOpen(false);
                              setLeaderSearch('');
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f7fafc',
                              borderRadius: '4px',
                              background: selected ? '#ebf4ff' : 'transparent',
                            }}
                          >
                            <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: selected ? 700 : 600 }}>
                              {user.name || 'Unnamed User'} ({user.role || '-'})
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Row 2: Start Date | Deadline */}
              <div>
                <div style={fieldLabelStyle}>Start Date</div>
                <input
                  type="date"
                  required
                  value={projectForm.startDate}
                  onChange={(e) => setProjectForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={fieldLabelStyle}>Deadline</div>
                <input
                  type="date"
                  required
                  min={projectForm.startDate || undefined}
                  value={projectForm.deadline}
                  onChange={(e) => setProjectForm((prev) => ({ ...prev, deadline: e.target.value }))}
                  style={inputStyle}
                />
              </div>

            <div ref={memberDropdownRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>Members</span>
                <label style={followerRadioLabelStyle}>
                  <input
                    type="radio"
                    name="memberSource"
                    checked={memberSource === 'hierarchy'}
                    onChange={() => {
                      setMemberSource('hierarchy');
                      setMemberSearch('');
                    }}
                  />
                  Hierarchy
                </label>
                <label style={followerRadioLabelStyle}>
                  <input
                    type="radio"
                    name="memberSource"
                    checked={memberSource === 'all'}
                    onChange={() => {
                      setMemberSource('all');
                      setMemberSearch('');
                    }}
                  />
                  All Users
                </label>
              </div>
              <button
                type="button"
                disabled={memberPickerLocked}
                onClick={() => setMemberDropdownOpen((prev) => !prev)}
                style={{
                  ...memberDropdownTriggerStyle,
                  cursor: memberPickerLocked ? 'not-allowed' : 'pointer',
                  opacity: memberPickerLocked ? 0.6 : 1,
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {memberPickerLocked
                    ? 'Select a project leader first'
                    : selectedUsers.length > 0
                    ? `${selectedUsers.length} member${selectedUsers.length > 1 ? 's' : ''} selected`
                    : 'Select members'}
                </span>
                <span style={{ fontSize: '12px', color: '#718096' }}>{memberDropdownOpen ? '▲' : '▼'}</span>
              </button>

              {memberDropdownOpen && !memberPickerLocked && (
                <div style={memberDropdownPanelStyle}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <input
                      placeholder="Search members..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                    />
                    {filteredUsers.length > 0 && (
                      <button type="button" onClick={toggleSelectAllMembers} style={{ ...selectAllBtnStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {allMembersSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: '220px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                    {filteredUsers.length === 0 && (
                      <div style={{ fontSize: '12px', color: '#718096', padding: '6px 2px' }}>
                        {memberSource === 'hierarchy' ? 'No one reports to this leader yet.' : 'No users found.'}
                      </div>
                    )}
                    {filteredUsers.map((user) => {
                      const userId = user._id || user.id;
                      const checked = projectForm.members.includes(userId);
                      return (
                        <label
                          key={userId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 2px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f7fafc',
                          }}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggleMember(userId)} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                              {user.name || 'Unnamed User'} ({user.role || '-'})
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {selectedUsers.length === 0 ? (
                  <span style={{ fontSize: '12px', color: '#718096' }}>No members selected</span>
                ) : (
                  <>
                    {selectedUsers.slice(0, 10).map((user) => (
                      <span
                        key={user._id || user.id}
                        style={{
                          background: '#ebf8ff',
                          color: '#2b6cb0',
                          border: '1px solid #bee3f8',
                          borderRadius: '999px',
                          padding: '3px 10px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {user.name}
                      </span>
                    ))}
                    {selectedUsers.length > 10 && (
                      <span style={moreChipStyle}>+{selectedUsers.length - 10} more</span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div ref={followerDropdownRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>Followers</span>
                <label style={followerRadioLabelStyle}>
                  <input
                    type="radio"
                    name="followerSource"
                    checked={followerSource === 'all'}
                    onChange={() => {
                      setFollowerSource('all');
                      setFollowerSearch('');
                    }}
                  />
                  All Users
                </label>
                <label style={followerRadioLabelStyle}>
                  <input
                    type="radio"
                    name="followerSource"
                    checked={followerSource === 'member'}
                    onChange={() => {
                      setFollowerSource('member');
                      setFollowerSearch('');
                    }}
                  />
                  Member
                </label>
              </div>
              <button
                type="button"
                onClick={() => setFollowerDropdownOpen((prev) => !prev)}
                style={memberDropdownTriggerStyle}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFollowers.length > 0
                    ? `${selectedFollowers.length} follower${selectedFollowers.length > 1 ? 's' : ''} selected`
                    : 'Select followers'}
                </span>
                <span style={{ fontSize: '12px', color: '#718096' }}>{followerDropdownOpen ? '▲' : '▼'}</span>
              </button>

              {followerDropdownOpen && (
                <div style={memberDropdownPanelStyle}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <input
                      placeholder="Search users..."
                      value={followerSearch}
                      onChange={(e) => setFollowerSearch(e.target.value)}
                      style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                    />
                    {filteredFollowerUsers.length > 0 && (
                      <button type="button" onClick={toggleSelectAllFollowers} style={{ ...selectAllBtnStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {allFollowersSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: '220px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                    {filteredFollowerUsers.length === 0 && (
                      <div style={{ fontSize: '12px', color: '#718096', padding: '6px 2px' }}>
                        {followerSource === 'member'
                          ? (projectForm.teamLeader
                              ? 'No team members under the selected leader.'
                              : 'Select a Project Leader first to see their team.')
                          : 'No users found.'}
                      </div>
                    )}
                    {filteredFollowerUsers.map((user) => {
                      const userId = user._id || user.id;
                      const checked = projectForm.followers.includes(userId);
                      return (
                        <label
                          key={userId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 2px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f7fafc',
                          }}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggleFollower(userId)} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                              {user.name || 'Unnamed User'} ({user.role || '-'})
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {selectedFollowers.length === 0 ? (
                  <span style={{ fontSize: '12px', color: '#718096' }}>No followers selected</span>
                ) : (
                  <>
                    {selectedFollowers.slice(0, 10).map((user) => (
                      <span
                        key={user._id || user.id}
                        style={{
                          background: '#faf5ff',
                          color: '#6b46c1',
                          border: '1px solid #e9d8fd',
                          borderRadius: '999px',
                          padding: '3px 10px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {user.name}
                      </span>
                    ))}
                    {selectedFollowers.length > 10 && (
                      <span style={moreChipStyle}>+{selectedFollowers.length - 10} more</span>
                    )}
                  </>
                )}
              </div>
            </div>

              {editingProjectId && (
                <div>
                  <div style={fieldLabelStyle}>Status</div>
                  <select
                    value={projectForm.status}
                    onChange={(e) => setProjectForm((prev) => ({ ...prev, status: e.target.value }))}
                    style={inputStyle}
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <textarea
              aria-label="Description"
              placeholder="Description"
              value={projectForm.description}
              onChange={(e) => setProjectForm((prev) => ({ ...prev, description: e.target.value }))}
              style={{ ...inputStyle, marginTop: '12px', width: '100%', minHeight: '90px', resize: 'vertical', paddingTop: '10px' }}
            />

            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={resetProjectModal} style={smallButtonStyle}>Cancel</button>
              <button
                type="button"
                onClick={handleSaveProject}
                disabled={savingProject}
                style={{
                  ...smallButtonStyle,
                  border: 'none',
                  color: '#fff',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {savingProject ? 'Saving...' : (editingProjectId ? 'Update Project' : 'Save Project')}
              </button>
            </div>
          </div>
        </div>
      )}

      {Boolean(confirmDeleteProjectId) && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            if (!deletingProjectId) setConfirmDeleteProjectId('');
          }}
        >
          <div
            style={{ ...modalCardStyle, width: '420px', maxWidth: '92vw' }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2d3748' }}>Delete Project</h3>
            <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to delete this project? It will be moved to the Trash, where you can restore it or delete it permanently.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteProjectId('')}
                disabled={Boolean(deletingProjectId)}
                style={smallButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteProject(confirmDeleteProjectId)}
                disabled={Boolean(deletingProjectId)}
                style={{
                  ...smallButtonStyle,
                  border: '1px solid #feb2b2',
                  background: '#fff5f5',
                  color: '#c53030',
                }}
              >
                {deletingProjectId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#ffffff'
};

const tdStyle = {
  padding: '10px',
  color: '#4a5568',
  fontSize: '13px',
  verticalAlign: 'top'
};

const actionBtnStyle = (background, color) => ({
  padding: '6px 10px',
  background,
  color,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600
});

const smallButtonStyle = {
  height: '30px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  background: '#fff',
  color: '#4a5568',
  fontSize: '12px',
  fontWeight: 600,
  padding: '0 10px',
  cursor: 'pointer',
};

const inputStyle = {
  width: '100%',
  height: '38px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: '#4a5568',
  fontWeight: 600,
  marginBottom: '4px',
};

const followerRadioLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '12px',
  color: '#4a5568',
  fontWeight: 600,
  cursor: 'pointer',
};

const memberDropdownTriggerStyle = {
  width: '100%',
  height: '38px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#fff',
  color: '#2d3748',
  cursor: 'pointer',
};

const memberDropdownPanelStyle = {
  position: 'absolute',
  top: '72px',
  left: 0,
  right: 0,
  zIndex: 20,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  padding: '10px',
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1200,
};

const modalCloseBtnStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: '24px',
  lineHeight: 1,
  cursor: 'pointer',
  color: '#718096',
  padding: '2px 6px',
};

const selectAllBtnStyle = {
  border: '1px solid #cbd5e0',
  background: '#f7fafc',
  color: '#2d3748',
  borderRadius: '6px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: '8px',
};

const moreChipStyle = {
  background: '#edf2f7',
  color: '#4a5568',
  border: '1px solid #cbd5e0',
  borderRadius: '999px',
  padding: '3px 10px',
  fontSize: '12px',
  fontWeight: 600,
};

const modalCardStyle = {
  width: '680px',
  maxWidth: '90vw',
  background: '#fff',
  borderRadius: '10px',
  padding: '16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
};

export default ProjectsListPage;